import HeadlineField from '@/components/customFields/HeadlineField';
import { StoryblokImage } from '@/components/customFields/StoryblokImage';
import { type ColorPluginData, HeadlineTag } from '@/components/customFields/types';
import { RichText } from '@/components/richText/RichText';
import { hasContent } from '@/helper/RichText';
import { buildAriaLabel } from '@/helper/buildAriaLabel';
import type { ComponentIconTextBoxStoryblok, PartialIconTextBoxStoryblok } from '@/types/component-types-sb';
import classNames from 'classnames';
import { Fragment } from 'react';

import classes from './IconTextBox.module.scss';

export type IconTextBoxItem = Pick<PartialIconTextBoxStoryblok, '_uid' | 'notes' | 'icon' | 'headline' | 'text'> & {
  color?: ColorPluginData;
};

// Use Pick to create IconTextBoxProps from ComponentIconTextBoxStoryblok
export type IconTextBoxProps = Pick<ComponentIconTextBoxStoryblok, 'headline' | 'layout'> & {
  items: IconTextBoxItem[];
  headlineClassName?: string;
  anchor?: string;
};

const IconTextBox: React.FC<IconTextBoxProps> = (props) => {
  let additionalInfoCount = 1;
  let additionalInfoShow = 1;

  const hasAdditionalInfo = props.items?.filter((item) => item.notes).length !== 0;
  return (
    <div id={props.anchor || undefined}>
      {props.headline && (
        <HeadlineField
          tag={HeadlineTag.H2}
          style={HeadlineTag.H3}
          className={classNames(classes.explanation, props.headlineClassName)}
        >
          {props.headline}
        </HeadlineField>
      )}
      <div className={classNames(classes.iconTextBoxes, props.layout === '4' ? classes.fourColumn : '')}>
        {props.items?.map((item) => {
          const headlineAriaLabel = buildAriaLabel(
            item.headline,
            hasContent(item.notes) && `siehe Fußnote ${additionalInfoShow}`
          );

          return (
            <div className={classes.iconTextBox} key={item._uid}>
              <div className={classes.header}>
                <div className={classNames(classes.icon, item.color ? item.color.name : 'blue100')}>
                  {item.icon && (
                    <StoryblokImage image={item.icon} width={{ pxMobile: 32, pxTablet: 32, pxDesktop: 32 }} />
                  )}
                </div>
                <HeadlineField
                  tag={HeadlineTag.H3}
                  style={HeadlineTag.H6}
                  className={classes.heading}
                  ariaLabel={headlineAriaLabel}
                >
                  {item.headline}
                  {hasContent(item.notes) && (
                    <sup aria-hidden="true" className={classes.additionalInfo}>
                      {additionalInfoShow++}
                    </sup>
                  )}
                </HeadlineField>
              </div>
              <RichText text={item.text} className={classes.text} />
            </div>
          );
        })}
      </div>
      {hasAdditionalInfo && (
        <>
          <hr className={classes.divider} />
          <div className={classes.footnote}>
            {props.items?.map((item) => (
              <Fragment key={item._uid}>
                {hasContent(item.notes) && (
                  <div className={classes.additionalContent} aria-label={'Fußnote ' + additionalInfoCount}>
                    <span aria-hidden="true">{additionalInfoCount++}</span> <RichText text={item.notes} />
                  </div>
                )}
              </Fragment>
            ))}
          </div>
        </>
      )}
    </div>
  );
};
export default IconTextBox;
